import { croodles } from '@dicebear/collection';
import { createAvatar } from '@dicebear/core';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'App.js';
import {
  getBonusMalusStudentsData,
  getBonusMalusTeachersData
} from 'api/bonusMalusApi';
import { addPoints, getPlayer } from 'api/playerApi.js';
import SubtleBadge from 'components/common/SubtleBadge.js';
import LoadingButton from 'components/fantaclasse/LoadingButton.js';
import { titler } from 'helpers/titler.js';
import Loading from 'pages/components/Loading.js';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Card,
  Col,
  Image,
  Row,
  Table,
  ToggleButton,
  ToggleButtonGroup
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import BonusMalusList from './BonusMalusList.js';

const AddPoints = ({ playerType }) => {
  titler('Aggiungi punti');
  const { id } = useParams();
  const [totalPoints, setTotalPoints] = useState(0);
  const [formData, setFormData] = useState({
    bonus: [],
    malus: []
  });
  const [currentTab, setCurrentTab] = useState(0);

  const {
    data: bonusMalusData,
    isSuccess,
    isLoading
  } = useQuery({
    queryKey: [
      playerType === 'students'
        ? 'studentsBonusMalusData'
        : 'teachersBonusMalusData'
    ],
    queryFn:
      playerType === 'students'
        ? getBonusMalusStudentsData
        : getBonusMalusTeachersData
  });

  const {
    data: playerData,
    isSuccess: isSuccessPlayerData,
    isError: isErrorPlayerData,
    error: playerError
  } = useQuery({
    queryKey: [id],
    queryFn: () => getPlayer(playerType, id)
  });

  const [avatar, setAvatar] = useState('');
  useEffect(() => {
    if (isSuccessPlayerData) {
      setAvatar(
        createAvatar(croodles, {
          seed: playerData.name,
          scale: 120
        }).toDataUriSync()
      );
    }
    if (isErrorPlayerData) {
      // Invalid player id or player not found
      toast.error(playerError.message);
      navigate('/classe');
    }
  }, [isSuccessPlayerData, isErrorPlayerData]);

  const updateTotalPoints = (scoreType, categoryId, scoreId, scoreObject) => {
    const foundFormIndex = formData[scoreType].findIndex(
      formObject =>
        formObject.category === categoryId && formObject.id === scoreId
    );

    const updatedArray = [...formData[scoreType]];
    const updatedFormObject = {
      category: categoryId,
      id: scoreId,
      quantity: scoreObject.quantity
    };

    if (foundFormIndex !== -1) {
      // Update existing object
      updatedArray[foundFormIndex] = {
        ...updatedArray[foundFormIndex],
        quantity: scoreObject.quantity
      };
    } else {
      // Add new object8
      updatedArray.push(updatedFormObject);
    }

    setFormData(prevFormData => ({
      ...prevFormData,
      [scoreType]: updatedArray
    }));

    setTotalPoints(totalPoints + scoreObject.value);
  };

  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: addPoints,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classroomData'] });
      queryClient.invalidateQueries({ queryKey: [id] });
      queryClient.invalidateQueries({ queryKey: ['feedData'] });
      queryClient.invalidateQueries({ queryKey: ['userData'] });
      toast.success(
        <span>
          Il punteggio di <strong>{playerData.name}</strong> è stato aggiornato.{' '}
        </span>
      );
      navigate('/classe');
    },
    onError: err => {
      toast.error(err.response?.data?.message || err.message);
    }
  });

  const handleSubmit = e => {
    e.preventDefault();
    mutation.mutate({
      playerType: playerType,
      playerCode: id,
      addPointsObject: formData
    });
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body className="text-center">
          <h2 className="mb-3 text-primary">
            <strong>Aggiungi BONUS e MALUS</strong>
          </h2>
          <h5 className="fw-light">
            Scegli tra quelli <strong>predefiniti</strong> o aggiungi i tuoi{' '}
            <strong>punteggi personalizzati</strong>!
          </h5>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col lg={8}>
          <Card>
            <Card.Header>
              <Row className="justify-content-between">
                <Col md="auto"></Col>
                <Col md="auto">
                  <ToggleButtonGroup
                    type="radio"
                    name="bonmal"
                    defaultValue={currentTab}
                    onChange={e => setCurrentTab(e)}
                  >
                    <ToggleButton
                      id="selectBonus"
                      value={0}
                      variant="outline-success"
                    >
                      Bonus
                    </ToggleButton>
                    <ToggleButton
                      id="selectMalus"
                      value={1}
                      variant="outline-danger"
                    >
                      Malus
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="px-0">
              {isLoading && <Loading />}
              {currentTab == 0 ? (
                <BonusMalusList
                  scoreType="bonus"
                  key={currentTab}
                  scoreList={isSuccess ? bonusMalusData.bonus : []}
                  formData={formData}
                  updateTotalPoints={updateTotalPoints}
                  playerType={playerType}
                />
              ) : (
                <BonusMalusList
                  scoreType="malus"
                  key={currentTab}
                  scoreList={isSuccess ? bonusMalusData.malus : []}
                  formData={formData}
                  updateTotalPoints={updateTotalPoints}
                  playerType={playerType}
                />
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <div className="sticky-sidebar">
            <Card>
              <Card.Body>
                <div className="text-center">
                  <div className="avatar avatar-4xl">
                    <Image src={avatar} />
                  </div>
                  <h4 className="mb-1">{playerData?.name}</h4>
                </div>
                <Table borderless className="fs-10">
                  <tbody>
                    <tr className="border-bottom">
                      <th className="ps-0">Punteggio attuale</th>
                      <th className="pe-0 text-end">
                        <SubtleBadge
                          pill
                          bg={
                            playerData?.score == 0
                              ? 'secondary'
                              : playerData?.score > 0
                              ? 'success'
                              : 'danger'
                          }
                          className="fs-10"
                        >
                          {playerData?.score > 0 ? '+' : ''}
                          {playerData?.score}
                        </SubtleBadge>
                      </th>
                    </tr>
                    <tr className="border-bottom">
                      <th className="ps-0">Bonus / Malus aggiunti</th>
                      <th className="pe-0 text-end">
                        <SubtleBadge
                          pill
                          bg={
                            totalPoints == 0
                              ? 'secondary'
                              : totalPoints > 0
                              ? 'success'
                              : 'danger'
                          }
                          className="fs-10"
                        >
                          {totalPoints > 0 ? '+' : ''}
                          {totalPoints}
                        </SubtleBadge>
                      </th>
                    </tr>
                  </tbody>
                </Table>
                <h4 className="d-flex justify-content-between">
                  <span>Totale</span>
                  <SubtleBadge
                    pill
                    bg={
                      playerData?.score + totalPoints == 0
                        ? 'secondary'
                        : playerData?.score + totalPoints > 0
                        ? 'success'
                        : 'danger'
                    }
                  >
                    {playerData?.score + totalPoints > 0 ? '+' : ''}
                    {playerData?.score + totalPoints} punti
                  </SubtleBadge>
                </h4>
                <LoadingButton
                  text="Conferma"
                  isLoading={mutation.isPending}
                  disabled={
                    formData.bonus.length == 0 && formData.malus.length == 0
                  }
                  className="d-block w-100"
                  action={handleSubmit}
                  icon={faCheck}
                />
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </>
  );
};

AddPoints.propTypes = {
  playerType: PropTypes.oneOf(['students', 'teachers'])
};

export default AddPoints;
